<script setup lang="ts">
import { ref, computed, reactive } from 'vue'
import { useAssignmentsStore } from '@/stores/assignments'
import { useIncidentStore } from '@/stores/incident'
import { useOpsStore } from '@/stores/ops'
import { useToastStore } from '@/stores/toast'
import type { Team, TeamAssignment, Assignment } from '@/types'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosTextarea from '@/components/ui/FosTextarea.vue'

const props = defineProps<{
  incidentId: string
  opId: string
  team: Team
}>()

const emit = defineEmits<{ close: [] }>()

const assignmentsStore = useAssignmentsStore()
const incidentStore = useIncidentStore()
const opsStore = useOpsStore()
const toast = useToastStore()

// ── State ──────────────────────────────────────────────────────────────────

type Step = 'list' | 'form'
const step = ref<Step>('list')
const selectedTa = ref<TeamAssignment | null>(null)
const saving = ref(false)

const form = reactive({
  // Field 4
  resource_type: '',
  // Field 5
  assignment_summary: '',
  // Field 6
  search_efforts: '',
  // Field 7
  areas_unsearched: '',
  // Field 8
  clues_found: '',
  // Field 9
  hazards_problems: '',
  // Field 10
  suggestions: '',
  // Fields 11–12
  time_entered: '',
  time_exited: '',
  // Field 14 — POD Summary
  pod_responsive_pct: 0,
  pod_unresponsive_pct: 0,
  pod_clues_pct: 0,
  // Field 15–16
  debriefer: '',
  debrief_date: '',
  // Summary
  summary_nothing_located: false,
  summary_useful_info: false,
  summary_potential_clues: false,
  assignment_completed: false,
  // Legacy (kept for backward compat)
  pod_percent: null as number | null,
  area_covered: '',
  notes: '',
})

// ── Computed ───────────────────────────────────────────────────────────────

const incidentName = computed(() => incidentStore.currentIncident?.incident_name ?? '—')
const opNumber = computed(() => opsStore.selectedOp?.op_number ?? '—')

/** All team_assignments for this team in the current OP */
const teamAssignments = computed(() => assignmentsStore.getTeamAssignments(props.team.team_id))

/** Assignments corresponding to this team's team_assignment records */
const assignmentList = computed(() =>
  teamAssignments.value
    .map((ta) => {
      const a = assignmentsStore.assignments.find((a) => a.assignment_id === ta.assignment_id)
      return a ? { ta, assignment: a } : null
    })
    .filter(Boolean) as { ta: TeamAssignment; assignment: Assignment }[],
)

const selectedAssignment = computed(() =>
  selectedTa.value
    ? assignmentsStore.assignments.find((a) => a.assignment_id === selectedTa.value!.assignment_id)
    : null,
)

const hasDebrief = (ta: TeamAssignment) => Object.keys(ta.debrief_cols ?? {}).length > 0

const timeSpent = computed(() => {
  if (!form.time_entered || !form.time_exited) return '—'
  const [eh, em] = form.time_entered.split(':').map(Number) as [number, number]
  const [xh, xm] = form.time_exited.split(':').map(Number) as [number, number]
  const enteredMins = eh * 60 + em
  const exitedMins = xh * 60 + xm
  const diff = exitedMins - enteredMins
  if (diff <= 0) return '—'
  const h = Math.floor(diff / 60)
  const m = diff % 60
  return h > 0 ? `${h}h ${m}m` : `${m}m`
})

// ── Helpers ────────────────────────────────────────────────────────────────

function toHHMM(date: Date): string {
  return date.toTimeString().slice(0, 5)
}

// ── Actions ────────────────────────────────────────────────────────────────

function selectAssignment(ta: TeamAssignment) {
  selectedTa.value = ta
  const cols = ta.debrief_cols ?? {}
  // Pre-fill all fields from existing debrief_cols
  const assignment = assignmentsStore.assignments.find((a) => a.assignment_id === ta.assignment_id)
  form.resource_type = (cols.resource_type as string) || props.team.team_type
  form.assignment_summary = (cols.assignment_summary as string) || assignment?.description || ''
  form.search_efforts = (cols.search_efforts as string) ?? ''
  form.areas_unsearched = (cols.areas_unsearched as string) ?? ''
  form.clues_found = (cols.clues_found as string) ?? ''
  form.hazards_problems = (cols.hazards_problems as string) ?? ''
  form.suggestions = (cols.suggestions as string) ?? ''
  form.time_entered = (cols.time_entered as string) || (ta.started_at ? toHHMM(ta.started_at.toDate()) : '')
  form.time_exited = (cols.time_exited as string) || (ta.completed_at ? toHHMM(ta.completed_at.toDate()) : '')
  form.pod_responsive_pct = (cols.pod_responsive_pct as number) ?? 0
  form.pod_unresponsive_pct = (cols.pod_unresponsive_pct as number) ?? 0
  form.pod_clues_pct = (cols.pod_clues_pct as number) ?? 0
  form.debriefer = (cols.debriefer as string) ?? ''
  form.debrief_date = (cols.debrief_date as string) ?? new Date().toLocaleDateString('en-NZ')
  form.summary_nothing_located = (cols.summary_nothing_located as boolean) ?? false
  form.summary_useful_info = (cols.summary_useful_info as boolean) ?? false
  form.summary_potential_clues = (cols.summary_potential_clues as boolean) ?? false
  form.assignment_completed = (cols.assignment_completed as boolean) ?? false
  // Legacy
  form.pod_percent = (cols.pod_percent as number | null) ?? null
  form.area_covered = (cols.area_covered as string) ?? ''
  form.notes = (cols.notes as string) ?? ''
  step.value = 'form'
}

function backToList() {
  step.value = 'list'
  selectedTa.value = null
}

async function submitDebrief() {
  if (!selectedTa.value) return
  saving.value = true
  try {
    await assignmentsStore.submitDebrief(props.incidentId, props.opId, selectedTa.value.ta_id, { ...form })
    toast.add('Debrief saved', 'success', 3000)
    backToList()
  } catch (e) {
    toast.add(String(e), 'error', 4000)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <FosDialog
    :open="true"
    :title="step === 'list' ? `Debrief — ${team.team_identifier}` : `Debrief: ${selectedAssignment?.assignment_name}`"
    @update:open="(v) => !v && emit('close')"
  >
    <!-- Assignment list step -->
    <div v-if="step === 'list'" class="py-1">
      <div class="flex items-center gap-2 mb-3">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest">Select an assignment to debrief</p>
      </div>

      <div v-if="assignmentList.length === 0" class="py-6 text-center text-sm text-outline font-mono">
        No assignments found for this team in this OP.
      </div>

      <ul v-else class="flex flex-col">
        <li
          v-for="{ ta, assignment } in assignmentList"
          :key="ta.ta_id"
          class="flex items-center justify-between py-3 cursor-pointer hover:bg-surface-container-low px-2 transition-colors"
          @click="selectAssignment(ta)"
        >
          <div>
            <p class="text-sm text-on-surface font-mono">{{ assignment.assignment_name }}</p>
            <p class="text-[10px] text-outline font-label uppercase tracking-widest mt-0.5">{{ assignment.assignment_status }}</p>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="hasDebrief(ta)"
              class="inline-flex items-center gap-1 text-xs text-primary font-mono"
            >
              <span class="material-symbols-outlined text-[14px]">check_circle</span>
              Debriefed
            </span>
            <span class="material-symbols-outlined text-[16px] text-outline">chevron_right</span>
          </div>
        </li>
      </ul>
    </div>

    <!-- Debrief form step -->
    <div v-else class="flex flex-col gap-0 py-1">
      <button
        type="button"
        class="flex items-center gap-1 text-[10px] text-outline font-label uppercase tracking-widest hover:text-on-surface-variant transition-colors w-fit mb-3"
        @click="backToList"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Back
      </button>

      <!-- SAR 110 header row -->
      <div class="grid grid-cols-3 gap-0 border border-border rounded-t mb-0 text-[10px] font-label uppercase tracking-widest">
        <div class="px-2 py-1.5 border-r border-border">
          <span class="text-outline">1. Incident</span>
          <p class="text-on-surface font-mono text-xs mt-0.5">{{ incidentName }}</p>
        </div>
        <div class="px-2 py-1.5 border-r border-border">
          <span class="text-outline">2. Op Period</span>
          <p class="text-on-surface font-mono text-xs mt-0.5">OP {{ opNumber }}</p>
        </div>
        <div class="px-2 py-1.5">
          <span class="text-outline">3. Assignment</span>
          <p class="text-on-surface font-mono text-xs mt-0.5">{{ selectedAssignment?.assignment_name ?? '—' }}</p>
        </div>
      </div>

      <!-- Field 4: Resource Type -->
      <div class="border border-t-0 border-border px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">4. Resource Type</label>
        <FosInput v-model="form.resource_type" placeholder="e.g. Ground Team, K9, Mounted" />
      </div>

      <!-- Field 5: Assignment Summary -->
      <div class="border border-t-0 border-border px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">5. Assignment Summary</label>
        <FosTextarea v-model="form.assignment_summary" :rows="3" :auto-resize="true" placeholder="Brief description of the assignment…" />
      </div>

      <!-- Field 6: Search Efforts -->
      <div class="border border-t-0 border-border px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">6. Describe Search Efforts in Assignment</label>
        <FosTextarea v-model="form.search_efforts" :rows="4" :auto-resize="true" placeholder="Describe the areas and methods used…" />
      </div>

      <!-- Field 7: Unable to Search -->
      <div class="border border-t-0 border-border px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">7. Describe Portions Unable to Search</label>
        <FosTextarea v-model="form.areas_unsearched" :rows="3" :auto-resize="true" placeholder="Areas not covered and reason…" />
      </div>

      <!-- Field 8: Clues/Tracks/Interviews -->
      <div class="border border-t-0 border-border px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">8. Clues, Tracks, Sign or Trail Interviews</label>
        <FosTextarea v-model="form.clues_found" :rows="3" :auto-resize="true" placeholder="Describe any clues or pertinent trail interviews…" />
      </div>

      <!-- Field 9: Hazards/Problems -->
      <div class="border border-t-0 border-border px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">9. Hazards or Problems Encountered</label>
        <FosTextarea v-model="form.hazards_problems" :rows="3" :auto-resize="true" placeholder="Injuries, hazards, equipment issues…" />
      </div>

      <!-- Field 10: Suggestions -->
      <div class="border border-t-0 border-border px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">10. Suggestions for Further Search Efforts</label>
        <FosTextarea v-model="form.suggestions" :rows="3" :auto-resize="true" placeholder="Recommendations for next OP…" />
      </div>

      <!-- Fields 11–13: Times -->
      <div class="border border-t-0 border-border px-3 py-2 grid grid-cols-3 gap-3">
        <div>
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">11. Time Entered</label>
          <FosInput v-model="form.time_entered" type="time" />
        </div>
        <div>
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">12. Time Exited</label>
          <FosInput v-model="form.time_exited" type="time" />
        </div>
        <div>
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">13. Time Spent</label>
          <p class="text-sm font-mono text-on-surface py-1.5">{{ timeSpent }}</p>
        </div>
      </div>

      <!-- Field 14: POD Summary -->
      <div class="border border-t-0 border-border px-3 py-3 flex flex-col gap-3">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">14. P.O.D. Summary</label>
        <div
          v-for="row in [
            { label: 'Responsive Subject', key: 'pod_responsive_pct' as const },
            { label: 'Unresponsive Subject', key: 'pod_unresponsive_pct' as const },
            { label: 'Clues', key: 'pod_clues_pct' as const },
          ]"
          :key="row.key"
          class="flex items-center gap-3"
        >
          <span class="text-xs text-on-surface-variant w-36 shrink-0">{{ row.label }}</span>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            :value="form[row.key]"
            class="flex-1 accent-primary cursor-pointer"
            @input="form[row.key] = Number(($event.target as HTMLInputElement).value)"
          />
          <span class="text-sm font-mono text-on-surface w-10 text-right tabular-nums">{{ form[row.key] }}%</span>
        </div>
      </div>

      <!-- Fields 15–16: Debriefer + Date -->
      <div class="border border-t-0 border-border px-3 py-2 grid grid-cols-2 gap-3">
        <div>
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">15. Debriefer</label>
          <FosInput v-model="form.debriefer" placeholder="Name of debriefer" />
        </div>
        <div>
          <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-1">16. Date & Time</label>
          <FosInput v-model="form.debrief_date" placeholder="dd/mm/yyyy" />
        </div>
      </div>


      <!-- Summary -->
      <div class="border border-t-0 border-border rounded-b px-3 py-2">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest block mb-2">Summary</label>
        <div class="grid grid-cols-2 gap-1.5">
          <label class="flex items-center gap-2 cursor-pointer text-sm text-on-surface">
            <input v-model="form.summary_nothing_located" type="checkbox" class="accent-primary" />
            Nothing Significant Located
          </label>
          <label class="flex items-center gap-2 cursor-pointer text-sm text-on-surface">
            <input v-model="form.summary_useful_info" type="checkbox" class="accent-primary" />
            Useful Information, Needs Review
          </label>
          <label class="flex items-center gap-2 cursor-pointer text-sm text-on-surface">
            <input v-model="form.summary_potential_clues" type="checkbox" class="accent-primary" />
            Potential Clues, Needs Urgent Review
          </label>
          <label class="flex items-center gap-2 cursor-pointer text-sm text-on-surface">
            <input v-model="form.assignment_completed" type="checkbox" class="accent-primary" />
            Assignment Completed
          </label>
        </div>
      </div>
    </div>

    <template #footer>
      <div v-if="step === 'form'" class="flex justify-end gap-2">
        <FosButton variant="tertiary" :disabled="saving" @click="backToList">Cancel</FosButton>
        <FosButton variant="primary" :disabled="saving" @click="submitDebrief">
          {{ saving ? 'Saving…' : 'Save Debrief' }}
        </FosButton>
      </div>
      <div v-else class="flex justify-end">
        <FosButton variant="tertiary" @click="emit('close')">Close</FosButton>
      </div>
    </template>
  </FosDialog>
</template>
